import * as vp from 'vopay';

import { format, subWeeks, parseISO } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { usePagination, useUpdateQueryParams } from 'vopay';
import useFetchPartnerAccountTransactions from '@/hooks/useApi/useFetchPartnerAccountTransactions';
import PartnerAccountList from './PartnerAccountList';
import TransactionsDialogue from './TransactionsDialogue';
import { DialogueHandlerProps, TransactionDialogueHandlers } from './types';
import TransactionsContext, { TransactionsCtx } from './TransactionsContext';

type QueryParams = {
  fromDate: string;
  toDate: string;
  vopayAccountID: string;
  currency: string;
  transactionType: string;
  status: string;
  transactionID: string;
};

const PAGE_SIZE = 20;

const PartnerAccountTransactions = (): React.JSX.Element => {
  const history = useHistory();
  const location = useLocation();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlFromDate = queryParams.get('fromDate') || '';
  const urlToDate = queryParams.get('toDate') || '';
  const urlVopayAccountID = queryParams.get('vopayAccountID') || '';
  const urlCurrency = queryParams.get('currency') || '';
  const urlTransactionType = queryParams.get('transactionType') || '';
  const urlStatus = queryParams.get('status') || '';
  const urlTransactionID = queryParams.get('transactionID') || '';

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const initialFilters = useMemo<vp.ParentAccountTransactionsRequest | undefined>(() => {
    if (!urlFromDate || !urlToDate) return undefined;
    return {
      FromDate: urlFromDate,
      ToDate: urlToDate,
      VopayAccountID: urlVopayAccountID || undefined,
      Currency: urlCurrency || undefined,
      TransactionType: urlTransactionType || undefined,
      Status: urlStatus || undefined,
      TransactionID: urlTransactionID || undefined,
    };
  }, []);

  const initialTimeRange = useMemo<[vp.Timestamp, vp.Timestamp] | undefined>(() => {
    if (!urlFromDate || !urlToDate) return undefined;
    return [parseISO(urlFromDate).getTime() as vp.Timestamp, parseISO(urlToDate).getTime() as vp.Timestamp];
  }, []);

  const [startDate, setStartDate] = useState(urlFromDate);
  const [endDate, setEndDate] = useState(urlToDate);
  const [areDatesValid, setAreDatesValid] = useState(false);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp] | undefined>();
  const [isSubmittingSearch, setIsSubmittingSearch] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [transactionsSearchData, setTransactionsSearchData] = useState<vp.ParentAccountTransactionsRequest | undefined>(
    initialFilters,
  );

  const [transactions, setTransactions] = useState<vp.AccountTransactionsMap>({});
  const [accountTransactionsList, fetchAccountTransactions] = useFetchPartnerAccountTransactions({});

  const { pageCount, pageIndex, setPageIndex } = usePagination(
    accountTransactionsList?.PaginationMetaData?.TotalRecords || 0,
    {
      pageSize: PAGE_SIZE,
    },
  );

  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [showTransactionsSearchSuccessAlert, setShowTransactionsSearchSuccessAlert] = useState(false);
  const [transactionsSuccessMessage, setTransactionsSuccessMessage] = useState<string>('');
  const [showTransactionsSearchErrorAlert, setShowTransactionsSearchErrorAlert] = useState(false);
  const [transactionsSearchStatus, setTransactionsSearchStatus] = useState<boolean | null>(null);
  const [transactionsSearchError, setTransactionsSearchError] = useState<string>('');

  // Auto-fetch on mount if URL has filter params
  useEffect(() => {
    if (initialFilters) {
      setIsSubmittingSearch(true);
    }
  }, []);

  useEffect(() => {
    if (timeRange) {
      const FromDate = format(new Date(timeRange[0]), 'yyyy-MM-dd');
      const ToDate = format(new Date(timeRange[1]), 'yyyy-MM-dd');

      setStartDate(FromDate);
      setEndDate(ToDate);
    }
  }, [timeRange]);

  useEffect(() => {
    setAreDatesValid(isDateValid(startDate) && isDateValid(endDate));
  }, [startDate, endDate]);

  useEffect(() => {
    if (transactionsSearchStatus === false) {
      setShowTransactionsSearchErrorAlert(true);
      setShowTransactionsSearchSuccessAlert(false);
    }
  }, [transactionsSearchStatus]);

  useEffect(() => {
    // if it is the first load of the page and we have saved transactions, get them from that state
    if (!isSubmittingSearch && !isExportingCSV) {
      return () => {};
    }

    const getTransactions = () => {
      // default values when no search has been done
      const currentDateTime = format(new Date(), 'yyyy-MM-dd');
      const timeDifference = format(subWeeks(new Date(), 1), 'yyyy-MM-dd');

      const defaultValues = {
        FromDate: timeDifference,
        ToDate: currentDateTime,
      };

      const accountTransactionRequest: vp.ParentAccountTransactionsRequest = {
        ...(transactionsSearchData
          ? {
              ...transactionsSearchData,
              TransactionType:
                transactionsSearchData.TransactionType === 'All' ? undefined : transactionsSearchData.TransactionType,
              ExportCSV: isExportingCSV ? true : false,
            }
          : defaultValues),
        FromDate: startDate || timeDifference,
        ToDate: endDate || currentDateTime,
      };

      if (!isExportingCSV) {
        setPageIndex(0);
        accountTransactionRequest.Limit = PAGE_SIZE;
        accountTransactionRequest.Offset = 0;
      }

      if (!hasInvalidEndDate) {
        fetchAccountTransactions(accountTransactionRequest);
      } else {
        setShouldShowEndDateError(true);
      }
    };

    return getTransactions();
  }, [isSubmittingSearch, isExportingCSV]);

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [endDate]);

  useEffect(() => {
    // if it is the first load of the page and we have saved transactions, get them from that state

    const proccessTransactionsData = () => {
      const { Transactions, CSVFile, Success, ErrorMessage } = accountTransactionsList;
      if (Transactions) {
        const transactionList = Object.values(Transactions);
        setTransactions((prevState: vp.AccountTransactionsMap) => ({
          ...prevState,
          transactionList,
        }));
        setTransactionsSearchStatus(Success);
        setTransactionsSearchError(ErrorMessage);
      }

      if (CSVFile) {
        const downloadLink = document.createElement('a');
        const fileName = 'account_transactions.csv';

        downloadLink.href = CSVFile;
        downloadLink.download = fileName;
        downloadLink.click();
      }

      if (!Success && ErrorMessage !== '') {
        setTransactionsSearchStatus(Success);
        setTransactionsSearchError(ErrorMessage);
      }

      setIsExportingCSV(false);
      setIsSubmittingSearch(false);
    };

    proccessTransactionsData();
  }, [accountTransactionsList]);

  function isDateValid(date: string) {
    if (!date) return true;

    const [, mm, dd] = date.split('-');

    const m = parseInt(mm, 10);
    const d = parseInt(dd, 10);

    const isValidMonth = m > 0 && m <= 12;
    const isValidDay = d > 0 && d <= 31 && dd.length === 2;

    return isValidMonth && isValidDay;
  }

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<TransactionDialogueHandlers>) => {
      const { type, state, action = 'default', hasNavigation } = handlerProps;

      const dialogueHandlers: TransactionDialogueHandlers = {
        search: {
          success: {
            default: () => {},
          },
          fail: {
            default: () => {
              setShowTransactionsSearchSuccessAlert(false);
              setShowTransactionsSearchErrorAlert(false);
              setTransactionsSuccessMessage('');
              setTransactionsSearchStatus(null);
            },
          },
        },
      };

      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();

      if (hasNavigation) history.push('/partner-account-transactions');
    },

    handleDate: (values: [vp.Timestamp, vp.Timestamp] | undefined) => {
      setTimeRange(values);
    },

    onTransactionsSearch: (values: vp.ParentAccountTransactionsRequest) => {
      const currentDateTime = format(new Date(), 'yyyy-MM-dd');
      const timeDifference = format(subWeeks(new Date(), 1), 'yyyy-MM-dd');

      updateQueryParams({
        fromDate: startDate || timeDifference,
        toDate: endDate || currentDateTime,
        vopayAccountID: values.VopayAccountID || '',
        currency: values.Currency || '',
        transactionType: values.TransactionType || '',
        status: values.Status || '',
        transactionID: values.TransactionID || '',
      });

      if (isExportingCSV) {
        setTransactionsSearchData({ ...values, ExportCSV: true });
      } else {
        setTransactionsSearchData(values);
        setIsSubmittingSearch(true);
      }
    },

    exportCSV: (): void => {
      setIsExportingCSV(true);
    },

    handlePageChange: (selectedPage: number): void => {
      const newOffset = setPageIndex(selectedPage);

      const currentDateTime = format(new Date(), 'yyyy-MM-dd');
      const timeDifference = format(subWeeks(new Date(), 1), 'yyyy-MM-dd');

      const req: vp.ParentAccountTransactionsRequest = {
        ...(transactionsSearchData
          ? {
              ...transactionsSearchData,
              TransactionType:
                transactionsSearchData.TransactionType === 'All' ? undefined : transactionsSearchData.TransactionType,
            }
          : {}),
        FromDate: startDate || timeDifference,
        ToDate: endDate || currentDateTime,
        Limit: PAGE_SIZE,
        Offset: newOffset,
      };

      fetchAccountTransactions(req);
    },
  };

  const transactionsContext: TransactionsCtx = {
    transactionsSearchStatus,
    transactionsSearchError,
    transactionsSuccessMessage,
    showTransactionsSearchSuccessAlert,
    showTransactionsSearchErrorAlert,
  };

  return (
    <TransactionsContext.Provider value={transactionsContext}>
      <PartnerAccountList
        areDatesValid={areDatesValid}
        transactions={transactions}
        handlers={handlers}
        pageCount={pageCount}
        pageIndex={pageIndex}
        isExportingCSV={isExportingCSV}
        hasInvalidEndDate={hasInvalidEndDate}
        shouldShowEndDateError={shouldShowEndDateError}
        setShouldShowEndDateError={setShouldShowEndDateError}
        initialFilters={initialFilters}
        initialTimeRange={initialTimeRange}
      />
      <TransactionsDialogue handleDialogue={handlers.handleDialogue} />
    </TransactionsContext.Provider>
  );
};

export default PartnerAccountTransactions;
